import {
  Component,
  ElementRef,
  HostListener,
  Input,
  computed,
  effect,
  forwardRef,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faCalendar,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { format, isValid, parse, startOfMonth } from 'date-fns';
import {
  NgpDatePicker,
  NgpDatePickerFirstDayOfWeekNumber,
  NgpDatePickerLabel,
  NgpDatePickerNextMonth,
  NgpDatePickerPreviousMonth,
} from 'ng-primitives/date-picker';

import {
  SUI_FORM_FIELD_CONTROL,
  SuiFormFieldControl,
} from '@sensika/sui/form-field';
import { SuiInputComponent } from '@sensika/sui/input';
import {
  SuiPopoverDirective,
  SuiPopoverTriggerDirective,
} from '@sensika/sui/popover';
import {
  SuiSelectComponent,
  SuiSelectOptionComponent,
} from '@sensika/sui/select';
import { SuiFormControlBase } from '@sensika/sui/shared';
import { resolveSuiBoundary } from '@sensika/sui/shared';
import { SuiCalendarComponent } from './sui-calendar.component';
import { SuiTimePickerComponent } from './sui-time-picker.component';

interface MonthOption {
  value: number;
  label: string;
  disabled: boolean;
}

/**
 * Combined date **and** time picker — the Session-A single calendar plus the
 * M1 `sui-time-picker`, editing one native `Date | null` (full datetime). Also
 * a **time-only mode** (`[mode]="'time'"`) that drops the calendar and shows
 * just the stepper — the replacement for `app-date-time-picker`'s
 * `hasDatePicker=false`.
 *
 * **One master value, two halves.** The calendar edits the date; the stepper
 * edits the time; each preserves the other's half:
 * - the primitive's `NgpDatePickerDateButton` selects with `preserveTime=true`,
 *   so clicking a day keeps the time-of-day (we feed it the *full* Date — and
 *   crucially do **not** `startOfDay`-normalize, the deliberate divergence from
 *   `sui-date-picker`);
 * - `sui-time-picker` preserves the date part of whatever Date is written to it,
 *   so changing the time keeps the day.
 * Both children bind the same `value()`, so a change to one re-feeds the other.
 *
 * Mirrors `sui-date-picker`'s field+popover+`[inline]` shape (parse-on-type
 * editable field, calendar `<ng-template>` declared inside `[ngpDatePicker]` so
 * the portaled popover copy still resolves the primitive's state). The popover
 * is a stay-open panel: a day-click does **not** close it (you still need to set
 * the time) — it closes on outside-click / Esc.
 */
@Component({
  selector: 'sui-date-time-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FaIconComponent,
    SuiCalendarComponent,
    SuiTimePickerComponent,
    SuiInputComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    SuiPopoverTriggerDirective,
    SuiPopoverDirective,
    NgpDatePicker,
    NgpDatePickerLabel,
    NgpDatePickerNextMonth,
    NgpDatePickerPreviousMonth,
  ],
  templateUrl: './sui-date-time-picker.component.html',
  styleUrl: './sui-date-time-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiDateTimePickerComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiDateTimePickerComponent),
    },
  ],
})
export class SuiDateTimePickerComponent
  extends SuiFormControlBase<Date>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-date-time-picker-${++SuiDateTimePickerComponent._idCounter}`;
  private _focused = false;
  /** Month to navigate to once the primitive view exists (buffers early writeValue). */
  private _pendingFocusMonth: Date | null = null;

  private readonly _datePicker = viewChild(NgpDatePicker);

  /** Mirrors the primitive's focused date (its state is protected) for the label. */
  private readonly _focusedDate = signal<Date>(new Date());

  /** The field input's text — formatted value, or the user's in-progress typing. */
  readonly inputText = signal<string>('');

  readonly controlType = 'date-time-picker';

  readonly faPrev = faAngleLeft;
  readonly faNext = faAngleRight;
  readonly faCalendar = faCalendar;
  readonly faClock = faClock;

  /** Month/year label, e.g. "June 2026" — the grid's accessible name. */
  readonly label = computed(() => format(this._focusedDate(), 'MMMM yyyy'));

  /** Selected date pushed into the primitive (`null` → undefined → no selection). */
  readonly primitiveDate = computed(() => this.value() ?? undefined);

  readonly triggerIcon = computed(() =>
    this.mode === 'time' ? this.faClock : this.faCalendar,
  );

  // ── Month/year quick-nav (header selects) ────────────────────────────────
  private readonly _monthNames = Array.from({ length: 12 }, (_, m) =>
    format(new Date(2024, m, 1), 'MMM'),
  );

  readonly focusedMonth = computed(() => this._focusedDate().getMonth());
  readonly focusedYear = computed(() => this._focusedDate().getFullYear());

  readonly monthOptions = computed<MonthOption[]>(() => {
    const year = this.focusedYear();
    return this._monthNames.map((label, value) => ({
      value,
      label,
      disabled:
        (!!this.min &&
          year === this.min.getFullYear() &&
          value < this.min.getMonth()) ||
        (!!this.max &&
          year === this.max.getFullYear() &&
          value > this.max.getMonth()),
    }));
  });

  readonly years = computed<number[]>(() => {
    const focused = this.focusedYear();
    const thisYear = new Date().getFullYear();
    let start = this.min
      ? this.min.getFullYear()
      : Math.min(thisYear, focused) - 10;
    let end = this.max
      ? this.max.getFullYear()
      : Math.max(thisYear, focused) + 10;
    start = Math.min(start, focused);
    end = Math.max(end, focused);
    const out: number[] = [];
    for (let y = start; y <= end; y++) {
      out.push(y);
    }
    return out;
  });

  @Input() id = '';
  /** `'datetime'` (calendar + time) or `'time'` (stepper only). */
  @Input() mode: 'datetime' | 'time' = 'datetime';

  // Date side.
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  @Input() dateDisabled: (date: Date) => boolean = () => false;
  @Input() firstDayOfWeek: NgpDatePickerFirstDayOfWeekNumber = 1;
  @Input() showWeekNumbers = false;

  // Time side.
  @Input() seconds = false;
  @Input() meridian = false;
  @Input() hourStep = 1;
  @Input() minuteStep = 1;
  @Input() secondStep = 1;

  /** Render directly (calendar+time, or just the stepper) instead of field+popover. */
  @Input() inline = false;
  @Input() placeholder = '';
  /** date-fns format for the field text. Empty → derived from mode/seconds/meridian. */
  @Input() displayFormat = '';
  /** Popover container override; defaults to the nearest boundary pane, else `body`. */
  @Input() popoverContainer?: string | HTMLElement;

  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  protected get resolvedPopoverContainer(): string | HTMLElement {
    return (
      this.popoverContainer ?? resolveSuiBoundary(this.hostEl.nativeElement)
    );
  }

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get minDate(): Date | undefined {
    return this.min ?? undefined;
  }
  get maxDate(): Date | undefined {
    return this.max ?? undefined;
  }

  /** Field display/parse format — derived when not set explicitly. */
  get effectiveFormat(): string {
    if (this.displayFormat) {
      return this.displayFormat;
    }
    const t = this.meridian
      ? this.seconds
        ? 'hh:mm:ss a'
        : 'hh:mm a'
      : this.seconds
        ? 'HH:mm:ss'
        : 'HH:mm';
    return this.mode === 'time' ? t : `dd MMM yyyy ${t}`;
  }

  constructor() {
    super();
    effect(() => {
      if (this._datePicker()) {
        this._applyFocusMonth();
      }
    });
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }
  get focused(): boolean {
    return this._focused;
  }
  get empty(): boolean {
    return !this.value();
  }
  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  override writeValue(value: Date | null): void {
    // No startOfDay — the time half is part of this control's value.
    this.value.set(value);
    // Never reformat under the user's caret: while the field has focus a valid
    // partial entry ("2026-09-1 23:59") already committed the value, and the
    // parent's write-back would canonicalise the text to "2026-09-01 23:59",
    // which moves the caret to the end (WAP-1664). Blur canonicalises.
    if (!this._fieldFocused) {
      this._syncInputText(value);
    }
    this._pendingFocusMonth = value;
    this._applyFocusMonth();
  }

  /** Calendar day-click — time-of-day already preserved by the primitive. */
  onDateChange(date: Date | undefined): void {
    const next = date ?? null;
    this.setValue(next);
    this._syncInputText(next);
    // Stay open: the user still needs to set the time (closes on outside/Esc).
  }

  /** Time stepper change — the stepper preserves the date part. */
  onTimeChange(date: Date | null): void {
    this.setValue(date);
    this._syncInputText(date);
  }

  /**
   * Field input (WAP-1664): free text with inline validation. While typing the
   * text belongs to the user - it is never rewritten under the caret - and the
   * value is committed only when the text is already canonical (round-trips
   * through the format), so a partial entry such as "2026-09-1 23:59" neither
   * commits an intermediate value nor gets padded to "2026-09-01" mid-word.
   * Anything parseable commits on Enter or blur; unparseable text paints the
   * field invalid and reverts to the committed value on blur.
   */
  onInputText(text: string): void {
    this.inputText.set(text);
    const trimmed = text.trim();
    if (!trimmed) {
      this.inputInvalid.set(false);
      this.setValue(null);
      return;
    }
    const parsed = this._parseInputText(trimmed);
    this.inputInvalid.set(parsed === null);
    if (parsed && format(parsed, this.effectiveFormat) === trimmed) {
      this._commitParsed(parsed);
    }
  }

  /** Enter commits whatever parses and canonicalises the text in place. */
  onInputEnter(): void {
    this._commitInputText();
  }

  /** True while the field input has focus: the text belongs to the user until blur. */
  private _fieldFocused = false;
  /** The current text does not parse in the display format. */
  readonly inputInvalid = signal(false);

  onInputFocus(): void {
    this._fieldFocused = true;
  }

  onInputBlur(): void {
    this._fieldFocused = false;
    this._commitInputText();
    this.markTouched();
  }

  private _parseInputText(text: string): Date | null {
    const parsed = parse(text, this.effectiveFormat, new Date());
    return isValid(parsed) ? parsed : null;
  }

  private _commitParsed(parsed: Date): void {
    this.setValue(parsed);
    this._datePicker()?.setFocusedDate(
      startOfMonth(parsed),
      'mouse',
      'forward',
    );
  }

  /** Commit a parseable text, then show the committed value canonically (reverts junk). */
  private _commitInputText(): void {
    const parsed = this._parseInputText(this.inputText().trim());
    if (parsed) {
      this._commitParsed(parsed);
    }
    this.inputInvalid.set(false);
    this._syncInputText(this.value());
  }

  private _syncInputText(value: Date | null): void {
    this.inputText.set(value ? format(value, this.effectiveFormat) : '');
  }

  onFocusedDateChange(date: Date): void {
    this._focusedDate.set(date);
  }

  onMonthChange(month: number): void {
    this._jumpTo(this.focusedYear(), month);
  }
  onYearChange(year: number): void {
    this._jumpTo(year, this.focusedMonth());
  }

  private _jumpTo(year: number, month: number): void {
    this._datePicker()?.setFocusedDate(
      startOfMonth(new Date(year, month, 1)),
      'mouse',
      'forward',
    );
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }
  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }

  private _applyFocusMonth(): void {
    const dp = this._datePicker();
    if (dp && this._pendingFocusMonth) {
      dp.setFocusedDate(
        startOfMonth(this._pendingFocusMonth),
        'mouse',
        'forward',
      );
      this._pendingFocusMonth = null;
    }
  }
}
